import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Eye, Download, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import QuoteDialog from "@/components/QuoteDialog";
import { imgProps } from "@/lib/images";

export default function ProductCard({ product }) {
  const [quickView, setQuickView] = useState(false);
  const [enquire, setEnquire] = useState(false);

  const brochureUrl = product.downloads?.[0]?.url || "#";

  return (
    <>
      <div className="gms-card block group relative overflow-hidden" data-testid={`product-card-${product.slug}`}>
        <Link to={`/products/${product.slug}`} className="block">
          <div className="aspect-[4/3] overflow-hidden bg-[#F5F7FA] border-b border-[#E2E8F0] relative">
            <img
              {...imgProps(product.images?.[0])}
              alt={`${product.name} — ${product.category}`}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-contain p-3"
              style={{ transition: "transform 500ms ease-out" }}
            />
            {/* Hover overlay with actions - subtle, professional. Brochure hidden until PDFs are ready. */}
            <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 bg-white/95 backdrop-blur-sm border-t border-[#E2E8F0] flex items-stretch"
                 style={{ transition: "transform 260ms ease-out" }}>
              <button
                onClick={(e) => { e.preventDefault(); setQuickView(true); }}
                className="flex-1 py-2.5 text-[11px] font-semibold text-[#0F3D66] hover:bg-[#F5F7FA] inline-flex items-center justify-center gap-1.5 transition-colors border-r border-[#E2E8F0]"
                data-testid={`quickview-${product.slug}`}
              >
                <Eye className="w-3.5 h-3.5" strokeWidth={1.75} /> Quick View
              </button>
              <button
                onClick={(e) => { e.preventDefault(); setEnquire(true); }}
                className="flex-1 py-2.5 text-[11px] font-semibold text-white bg-[#007BFF] hover:bg-[#0056b3] inline-flex items-center justify-center gap-1.5 transition-colors"
                data-testid={`enquire-${product.slug}`}
              >
                <Mail className="w-3.5 h-3.5" strokeWidth={1.75} /> Enquire Now
              </button>
            </div>
          </div>
          <div className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="gms-eyebrow mb-1.5 truncate">{product.brand}{product.sub_category ? ` · ${product.sub_category}` : product.category ? ` · ${product.category}` : ""}</div>
                <h3 className="font-heading font-bold text-[#0F3D66] text-lg leading-snug">{product.name}</h3>
              </div>
              <ArrowUpRight className="w-4 h-4 text-[#007BFF] shrink-0 mt-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={2}/>
            </div>
            {product.max_speed && (
              <div className="mt-3 flex items-baseline gap-2 border-t border-[#E2E8F0] pt-3">
                <span className="text-[10px] uppercase tracking-widest font-bold text-[#5F6B76]">{product.speed_label || "Max Speed"}</span>
                <span className="font-heading font-extrabold text-[#007BFF] text-sm" data-testid={`product-speed-${product.slug}`}>{product.max_speed}</span>
              </div>
            )}
            <p className="text-sm text-[#5F6B76] mt-3 leading-relaxed line-clamp-2">{product.short_description}</p>
          </div>
        </Link>
      </div>

      {/* Quick View Dialog */}
      <Dialog open={quickView} onOpenChange={setQuickView}>
        <DialogContent className="sm:max-w-3xl bg-white border border-[#E2E8F0] rounded-sm p-0 overflow-hidden" data-testid="quickview-dialog">
          <div className="grid md:grid-cols-2">
            <div className="aspect-square md:aspect-auto bg-[#F5F7FA] overflow-hidden flex items-center justify-center">
              <img {...imgProps(product.images?.[0], "(min-width: 768px) 384px, 100vw")} alt={`${product.name} — ${product.category}`} decoding="async" className="w-full h-full object-contain p-4" />
            </div>
            <div className="p-6 md:p-8">
              <DialogHeader>
                <div className="gms-eyebrow mb-2">{product.brand} · {product.category}</div>
                <DialogTitle className="font-heading text-2xl text-[#0F3D66] leading-tight text-left">{product.name}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-[#5F6B76] mt-3 leading-relaxed">{product.short_description}</p>
              {product.key_features?.length > 0 && (
                <ul className="mt-4 space-y-1.5">
                  {product.key_features.slice(0,4).map((f,i)=>(
                    <li key={i} className="text-xs text-[#5F6B76] flex gap-2"><span className="text-[#007BFF]">•</span>{f}</li>
                  ))}
                </ul>
              )}
              <div className="mt-6 flex flex-wrap gap-2">
                <Link to={`/products/${product.slug}`} className="gms-btn-primary text-sm" data-testid="quickview-view-details">View Details</Link>
                <button onClick={() => { setQuickView(false); setEnquire(true); }} className="gms-btn-secondary text-sm" data-testid="quickview-enquire">Enquire Now</button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <QuoteDialog open={enquire} onOpenChange={setEnquire} productSlug={product.slug} leadType="quote" title={`Enquire — ${product.name}`} />
    </>
  );
}
